import { useMemo } from 'react';
import type { ApplicationLicenseStates } from '@jira/platform__license-state/src/license-state.tsx';
import { useAssetsWorkspaceHost } from '@atlassian/assets-workspace-host/use-assets-workspace-host';
import { getProductContext } from '@atlassian/assets-workspace-host/get-product-context';
import {
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

export type RenderableShortcutInputs = {
	licenceStates: ApplicationLicenseStates;
	hasConfluence: boolean;
	hasLoom: boolean;
	hasGoal: boolean;
	hasProject: boolean;
	/**
	 * Whether the Teams app shortcut is supported in the current render. Maps to
	 * `!!teamsAppConfig` inside `useLicenseFilterMap`. From a parent component
	 * (e.g. `ConfigurableMenuItems.tsx`) this is `isTeamsAppEnabled()` — the same
	 * predicate that decides which `GlobalShortcuts` wrapper to render.
	 */
	teamsSupported: boolean;
	/**
	 * Result of `expVal('jira_nav_shortcuts_projects_visibility', 'isEnabled', false)`
	 * passed in so the helper can stay a pure function (no global experiment lookup).
	 */
	projectsExperimentEnabled: boolean;
	/**
	 * Whether the cross-unit `astral_units_workspace_host_resolver` succeeded — i.e.
	 * Assets is reachable on a sibling workspace even though no JSM licence exists
	 * locally. Mirrors `isAssetsResolverSuccess` in `useLicenseFilterMap`. When true
	 * the fall-through branch (no recognised licence state) includes Assets.
	 */
	assetsResolverSuccess: boolean;
};

/**
 * Returns the global-app menu ids the sidebar's shortcut registry
 * (`useLicenseFilterMap`) is currently capable of rendering.
 *
 * This must stay in lockstep with `useLicenseFilterMap`'s key set — it IS the source
 * of truth for that key set, and `useLicenseFilterMap` defers to it. The reason the
 * helper exists separately is so `getAppItemsData` consumers (the customisation modal)
 * can perform the same intersection without involving React hooks or child→parent
 * state reporting (which can cause render cycles).
 *
 * Pure function — no React, no global state — so it's safe to call from anywhere
 * (other helpers, tests, render-pure code paths).
 */
export const getRenderableShortcutMenuIds = ({
	licenceStates,
	hasConfluence,
	hasLoom,
	hasGoal,
	hasProject,
	teamsSupported,
	projectsExperimentEnabled,
	assetsResolverSuccess,
}: RenderableShortcutInputs): string[] => {
	// Inside any of the four explicit licence-state branches, Projects is gated on the
	// `jira_nav_shortcuts_projects_visibility` experiment; in the fall-through branch
	// (no recognised licence state) Projects is included on `hasProject` alone.
	const projectsInBranch = hasProject && projectsExperimentEnabled;

	if (licenceStates.core === 'ACTIVE') {
		if (licenceStates.serviceDesk === 'ACTIVE') {
			return [
				...(hasConfluence ? [L1_MENU_ID.GLOBAL_APP_CONFLUENCE] : []),
				...(hasLoom ? [L1_MENU_ID.GLOBAL_APP_LOOM] : []),
				L1_MENU_ID.GLOBAL_APP_ASSETS,
				L1_MENU_ID.GLOBAL_APP_OPSGENIE,
				...(teamsSupported ? [L1_MENU_ID.GLOBAL_APP_TEAMS] : []),
				...(hasGoal ? [L1_MENU_ID.GLOBAL_APP_GOALS] : []),
				...(projectsInBranch ? [L1_MENU_ID.GLOBAL_APP_PROJECTS] : []),
			];
		}

		if (licenceStates.productDiscovery === 'ACTIVE') {
			return [
				...(hasConfluence ? [L1_MENU_ID.GLOBAL_APP_CONFLUENCE] : []),
				...(hasLoom ? [L1_MENU_ID.GLOBAL_APP_LOOM] : []),
				...(teamsSupported ? [L1_MENU_ID.GLOBAL_APP_TEAMS] : []),
				...(hasGoal ? [L1_MENU_ID.GLOBAL_APP_GOALS] : []),
				...(projectsInBranch ? [L1_MENU_ID.GLOBAL_APP_PROJECTS] : []),
			];
		}
	} else {
		if (licenceStates.serviceDesk === 'ACTIVE') {
			return [
				L1_MENU_ID.GLOBAL_APP_ASSETS,
				L1_MENU_ID.GLOBAL_APP_OPSGENIE,
				...(teamsSupported ? [L1_MENU_ID.GLOBAL_APP_TEAMS] : []),
				...(hasGoal ? [L1_MENU_ID.GLOBAL_APP_GOALS] : []),
				...(projectsInBranch ? [L1_MENU_ID.GLOBAL_APP_PROJECTS] : []),
			];
		}

		if (licenceStates.productDiscovery === 'ACTIVE') {
			return [
				...(teamsSupported ? [L1_MENU_ID.GLOBAL_APP_TEAMS] : []),
				...(hasGoal ? [L1_MENU_ID.GLOBAL_APP_GOALS] : []),
				...(projectsInBranch ? [L1_MENU_ID.GLOBAL_APP_PROJECTS] : []),
			];
		}
	}

	return [
		...(hasConfluence ? [L1_MENU_ID.GLOBAL_APP_CONFLUENCE] : []),
		...(hasLoom ? [L1_MENU_ID.GLOBAL_APP_LOOM] : []),
		// Cross-unit Assets fallthrough: include Assets when reachable on a sibling
		// workspace even without a local JSM licence. Matches `useLicenseFilterMap`'s
		// fall-through branch which includes Assets on `isAssetsResolverSuccess`.
		...(assetsResolverSuccess ? [L1_MENU_ID.GLOBAL_APP_ASSETS] : []),
		...(teamsSupported ? [L1_MENU_ID.GLOBAL_APP_TEAMS] : []),
		...(hasGoal ? [L1_MENU_ID.GLOBAL_APP_GOALS] : []),
		...(hasProject ? [L1_MENU_ID.GLOBAL_APP_PROJECTS] : []),
	];
};

/**
 * Hook wrapper around `getRenderableShortcutMenuIds` that pulls all the licence /
 * product / experiment inputs from context. Use this from any render-pure context
 * that needs to know which shortcut menu ids the sidebar can render — for example,
 * `ConfigurableMenuItems.tsx` uses it to intersect `appItemsData` before passing it
 * to the customisation modal so the modal cannot list ids the sidebar drops.
 *
 * @param teamsSupported the parent's view of whether Teams is supported. Pass
 *   `isTeamsAppEnabled()` from `@jira/platform__teams-app-config` — the same
 *   predicate the parent uses to choose the Teams-aware shortcuts wrapper.
 */
export const useRenderableShortcutMenuIds = (teamsSupported: boolean): string[] => {
	const licenceStates = useLicenseStates();
	const products = getLicensedProducts();
	const hasConfluence = !!products[LICENSED_PRODUCTS.CONFLUENCE];
	const hasLoom = !!products[LICENSED_PRODUCTS.LOOM];
	const hasProject =
		!!products[LICENSED_PRODUCTS.PROJECT] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];
	const hasGoal = !!products[LICENSED_PRODUCTS.GOAL] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];

	// Mirror `useLicenseFilterMap`'s cross-unit Assets resolver wiring so the modal's
	// renderable key set matches the sidebar registry exactly.
	const cloudId = useCloudId();
	const appPermissions = useApplicationPermissions();
	const workspaceHostState = useAssetsWorkspaceHost({
		cloudId,
		productContext: getProductContext({ appPermissions }),
		skip: !fg('astral_units_workspace_host_resolver'),
	});
	const assetsResolverSuccess =
		workspaceHostState.status === 'success' && fg('astral_units_workspace_host_resolver');

	return useMemo(
		() =>
			getRenderableShortcutMenuIds({
				licenceStates,
				hasConfluence,
				hasLoom,
				hasGoal,
				hasProject,
				teamsSupported,
				projectsExperimentEnabled: expVal(
					'jira_nav_shortcuts_projects_visibility',
					'isEnabled',
					false,
				),
				assetsResolverSuccess,
			}),
		[
			licenceStates,
			hasConfluence,
			hasLoom,
			hasGoal,
			hasProject,
			teamsSupported,
			assetsResolverSuccess,
		],
	);
};
